import type { Viewport } from '@deck.gl/core';
import { TileLayer, _Tileset2D as Tileset2D } from '@deck.gl/geo-layers';
import { BitmapLayer } from '@deck.gl/layers';
import type { BitmapBoundingBox, BitmapLayerProps } from '@deck.gl/layers';
import { parse } from '@loaders.gl/core';
import proj4 from 'proj4';

import type { renderBitmapTileSubLayer } from './renderBitmapTileSubLayer';

const KROVAK_DEFINITION =
  '+proj=krovak +lat_0=49.5 +lon_0=24.8333333333333 ' +
  '+alpha=30.2881397527778 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel ' +
  // Rotations use the position-vector sign convention that proj4 +towgs84
  // expects. The published EPSG:5514 values are coordinate-frame (negated); the
  // opposite sign shifts every reprojected tile ~35m east. These signs match
  // PROJ's S-JTSK->WGS84 (1)/(5) to <0.1m and the WGS84 tiles and GPS markers.
  '+towgs84=572.213,85.334,461.94,4.9732,1.529,5.2484,3.5378 ' +
  '+units=m +no_defs +type=crs';

const CENIA_WMTS_URL =
  'https://gis.cenia.cz/mapcache/ortofotomapa_historicka/wmts/1.0.0/' +
  'orto/default/jtsk:epsg:5514';
const CENIA_TILE_SIZE = 512;
const CENIA_MAX_ZOOM = 12;
const CENIA_TOP_LEFT_X = -925000;
const CENIA_TOP_LEFT_Y = -920000;
const CENIA_EXTENT_SIZE = 524353.536;
const CENIA_WEB_ZOOM_OFFSET = 7;
const REPROJECTION_MESH_SEGMENTS = 16;

const CENIA_GEOGRAPHIC_EXTENT = {
  west: 12.09,
  south: 48.551,
  east: 18.86,
  north: 51.056,
};

export const HISTORICAL_CENIA_ATTRIBUTION =
  'Historical orthophoto © CENIA 2010 and © GEODIS BRNO, spol. s r.o. 2010; ' +
  'source aerial imagery © VGHMÚř Dobruška and © MO ČR 2009 ' +
  '(<a href="https://creativecommons.org/licenses/by/4.0/" target="_blank">CC BY 4.0</a>)';

interface TileIndex {
  x: number;
  y: number;
  z: number;
}

type JtskBounds = [left: number, bottom: number, right: number, top: number];

interface CeniaTileMetadata {
  jtskBounds: JtskBounds;
  geographicCorners: [
    [number, number],
    [number, number],
    [number, number],
    [number, number]
  ];
}

interface HistoricalCeniaBitmapProps {
  jtskBounds: JtskBounds;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

function wgs84ToJtsk(
  longitude: number,
  latitude: number
): [number, number] {
  const [easting, northing] = proj4('EPSG:4326', KROVAK_DEFINITION, [
    longitude,
    latitude,
  ]);

  return [easting, northing];
}

function jtskToWgs84(
  easting: number,
  northing: number
): [number, number] {
  const [longitude, latitude] = proj4(KROVAK_DEFINITION, 'EPSG:4326', [
    easting,
    northing,
  ]);

  return [longitude, latitude];
}

function getJtskTileBounds({ x, y, z }: TileIndex): JtskBounds {
  const span = CENIA_EXTENT_SIZE / 2 ** z;
  const left = CENIA_TOP_LEFT_X + x * span;
  const top = CENIA_TOP_LEFT_Y - y * span;

  return [left, top - span, left + span, top];
}

function getGeographicCorners(
  jtskBounds: JtskBounds
): CeniaTileMetadata['geographicCorners'] {
  const [left, bottom, right, top] = jtskBounds;

  return [
    jtskToWgs84(left, bottom),
    jtskToWgs84(left, top),
    jtskToWgs84(right, top),
    jtskToWgs84(right, bottom),
  ];
}

function getGeographicBbox(corners: CeniaTileMetadata['geographicCorners']) {
  const longitudes = corners.map(([longitude]) => longitude);
  const latitudes = corners.map(([, latitude]) => latitude);

  return {
    west: Math.min(...longitudes),
    south: Math.min(...latitudes),
    east: Math.max(...longitudes),
    north: Math.max(...latitudes),
  };
}

function getNativeZoom(viewportZoom: number): number {
  return clamp(
    Math.round(viewportZoom - CENIA_WEB_ZOOM_OFFSET),
    0,
    CENIA_MAX_ZOOM
  );
}

export function getHistoricalCeniaTileIndices(
  viewportBounds: [west: number, south: number, east: number, north: number],
  viewportZoom: number
): TileIndex[] {
  const [viewportWest, viewportSouth, viewportEast, viewportNorth] =
    viewportBounds;
  const west = Math.max(viewportWest, CENIA_GEOGRAPHIC_EXTENT.west);
  const south = Math.max(viewportSouth, CENIA_GEOGRAPHIC_EXTENT.south);
  const east = Math.min(viewportEast, CENIA_GEOGRAPHIC_EXTENT.east);
  const north = Math.min(viewportNorth, CENIA_GEOGRAPHIC_EXTENT.north);

  if (west >= east || south >= north) {
    return [];
  }

  const projectedCorners = [
    wgs84ToJtsk(west, south),
    wgs84ToJtsk(west, north),
    wgs84ToJtsk(east, north),
    wgs84ToJtsk(east, south),
  ];
  const eastings = projectedCorners.map(([easting]) => easting);
  const northings = projectedCorners.map(([, northing]) => northing);
  const minimumEasting = Math.min(...eastings);
  const maximumEasting = Math.max(...eastings);
  const minimumNorthing = Math.min(...northings);
  const maximumNorthing = Math.max(...northings);
  const z = getNativeZoom(viewportZoom);
  const matrixSize = 2 ** z;
  const tileSpan = CENIA_EXTENT_SIZE / matrixSize;
  const firstX = clamp(
    Math.floor((minimumEasting - CENIA_TOP_LEFT_X) / tileSpan),
    0,
    matrixSize - 1
  );
  const lastX = clamp(
    Math.ceil((maximumEasting - CENIA_TOP_LEFT_X) / tileSpan) - 1,
    0,
    matrixSize - 1
  );
  const firstY = clamp(
    Math.floor((CENIA_TOP_LEFT_Y - maximumNorthing) / tileSpan),
    0,
    matrixSize - 1
  );
  const lastY = clamp(
    Math.ceil((CENIA_TOP_LEFT_Y - minimumNorthing) / tileSpan) - 1,
    0,
    matrixSize - 1
  );
  const indices: TileIndex[] = [];

  for (let x = firstX; x <= lastX; x += 1) {
    for (let y = firstY; y <= lastY; y += 1) {
      indices.push({ x, y, z });
    }
  }

  return indices;
}

class HistoricalCeniaTileset extends Tileset2D {
  getTileIndices({ viewport }: { viewport: Viewport }): TileIndex[] {
    const bounds = viewport.getBounds() as [number, number, number, number];

    return getHistoricalCeniaTileIndices(bounds, viewport.zoom);
  }

  getTileMetadata(index: TileIndex) {
    const jtskBounds = getJtskTileBounds(index);
    const geographicCorners = getGeographicCorners(jtskBounds);
    const userData: CeniaTileMetadata = { jtskBounds, geographicCorners };

    return {
      bbox: getGeographicBbox(geographicCorners),
      userData,
    };
  }
}

function createReprojectedMesh(jtskBounds: JtskBounds) {
  const [left, bottom, right, top] = jtskBounds;
  const vertexSide = REPROJECTION_MESH_SEGMENTS + 1;
  const vertexCount = vertexSide * vertexSide;
  const positions = new Float64Array(vertexCount * 3);
  const texCoords = new Float32Array(vertexCount * 2);
  const indices = new Uint32Array(
    REPROJECTION_MESH_SEGMENTS * REPROJECTION_MESH_SEGMENTS * 6
  );
  let vertex = 0;
  let index = 0;

  for (let x = 0; x < vertexSide; x += 1) {
    const u = x / REPROJECTION_MESH_SEGMENTS;

    for (let y = 0; y < vertexSide; y += 1) {
      const v = y / REPROJECTION_MESH_SEGMENTS;
      const easting = left + (right - left) * u;
      const northing = bottom + (top - bottom) * v;
      const [longitude, latitude] = jtskToWgs84(easting, northing);

      positions[vertex * 3] = longitude;
      positions[vertex * 3 + 1] = latitude;
      positions[vertex * 3 + 2] = 0;
      texCoords[vertex * 2] = u;
      texCoords[vertex * 2 + 1] = 1 - v;

      if (x > 0 && y > 0) {
        indices[index] = vertex - vertexSide;
        indices[index + 1] = vertex - vertexSide - 1;
        indices[index + 2] = vertex - 1;
        indices[index + 3] = vertex - vertexSide;
        indices[index + 4] = vertex - 1;
        indices[index + 5] = vertex;
        index += 6;
      }

      vertex += 1;
    }
  }

  return {
    vertexCount: indices.length,
    positions,
    indices,
    texCoords,
  };
}

class HistoricalCeniaBitmapLayer extends BitmapLayer<HistoricalCeniaBitmapProps> {
  protected _createMesh() {
    return createReprojectedMesh(this.props.jtskBounds);
  }
}

type BitmapTileSubLayerProps = Parameters<
  typeof renderBitmapTileSubLayer
>[0];

function renderHistoricalCeniaSubLayer(props: BitmapTileSubLayerProps) {
  const { data, ...parentProps } = props;
  const metadata = props.tile.userData as CeniaTileMetadata;

  return [
    new HistoricalCeniaBitmapLayer(parentProps, {
      image: data as NonNullable<BitmapLayerProps['image']>,
      bounds: metadata.geographicCorners as BitmapBoundingBox,
      jtskBounds: metadata.jtskBounds,
    }),
  ];
}

export const historicalCeniaLayer = () =>
  new TileLayer({
    id: 'historical-cenia-layer',
    TilesetClass: HistoricalCeniaTileset,
    getTileData: async ({ index: { x, y, z }, signal }) => {
      const url = `${CENIA_WMTS_URL}/${z}/${y}/${x}.jpgpng`;
      const response = await fetch(url, { signal });

      if (!response.ok) {
        throw new Error(`CENIA tile request failed with ${response.status}`);
      }

      return parse(response);
    },
    maxRequests: 17,
    pickable: true,
    highlightColor: [60, 60, 60, 40],
    minZoom: 0,
    maxZoom: CENIA_MAX_ZOOM,
    tileSize: CENIA_TILE_SIZE,
    opacity: 1,
    renderSubLayers: renderHistoricalCeniaSubLayer,
  });
